import { api } from "../lib";

export type SlidePrintRoutingMode =
  | "backend_only"
  | "local_with_backend_fallback";

export interface SlidePrintRoutingSettings {
  mode: SlidePrintRoutingMode;
  updatedAt: string | null;
  changed?: boolean;
}

export interface UpdateSlidePrintRoutingSettingsRequest {
  mode: SlidePrintRoutingMode;
  expectedUpdatedAt: string | null;
}

interface HttpError {
  response?: { status?: number };
}

function isRoutingMode(candidate: unknown): candidate is SlidePrintRoutingMode {
  return (
    candidate === "backend_only" || candidate === "local_with_backend_fallback"
  );
}

function translateSettingsError(error: unknown): Error {
  const status = (error as HttpError).response?.status;
  if (status === 409) {
    return new Error(
      "These settings changed while you were editing. Review the latest value and try again."
    );
  }
  if (status === 403) {
    return new Error("You do not have permission to manage slide printing.");
  }
  return new Error(
    "Slide printing settings are unavailable. Check your connection and try again."
  );
}

async function getSettings(): Promise<SlidePrintRoutingSettings> {
  try {
    const settings = await api.get<SlidePrintRoutingSettings>(
      "/slide-print-routing",
      { headers: { "Cache-Control": "no-cache" } }
    );
    if (!isRoutingMode(settings.mode)) throw new Error("Invalid routing mode");
    return settings;
  } catch (error) {
    throw translateSettingsError(error);
  }
}

async function resolveModeForPrint(): Promise<SlidePrintRoutingMode> {
  try {
    return (await getSettings()).mode;
  } catch {
    return "backend_only";
  }
}

async function updateSettings(
  request: UpdateSlidePrintRoutingSettingsRequest
): Promise<SlidePrintRoutingSettings> {
  try {
    return await api.put<SlidePrintRoutingSettings>(
      "/slide-print-routing",
      request
    );
  } catch (error) {
    throw translateSettingsError(error);
  }
}

export const slidePrintRoutingService = {
  get: getSettings,
  resolveModeForPrint,
  update: updateSettings,
};
